import { useEffect, useMemo, useState } from "react"
import {
  getGatedCommunityMembers,
  type GatedCommunityMemberRecord,
  type GatedCommunityMembersSortBy
} from "src/services/api/gatedCommunities"
import { getInvitations } from "src/services/api/companyMembers"
import type { Role } from "src/UserService"
import { useCompany, useCommunities } from "src/providers"
import { filterMembersByQuery, filterMembersByRoles, sortMembersByField } from "./companyMembersUtils"

export interface UseGatedCommunityMembersFetchParams {
  gatedCommunityId: string | undefined
  page: number
  rowsPerPage: number
  orderBy: GatedCommunityMembersSortBy
  order: "asc" | "desc"
  name: string
  roles: Role[]
  refreshKey: number
  enabled?: boolean
}

export function useGatedCommunityMembersFetch({
  gatedCommunityId,
  page,
  rowsPerPage,
  orderBy,
  order,
  name,
  roles,
  refreshKey,
  enabled = true
}: UseGatedCommunityMembersFetchParams) {
  const { isOrgAdmin, companyId } = useCompany()
  const { communities } = useCommunities()
  const [allMembers, setAllMembers] = useState<GatedCommunityMemberRecord[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!gatedCommunityId || !enabled) {
      setLoading(false)
      setAllMembers([])
      return
    }
    setLoading(true)

    const membersFetch = getGatedCommunityMembers(gatedCommunityId, { limit: 9999 })
    const invitationsFetch = isOrgAdmin ? getInvitations(companyId, communities) : Promise.resolve([])

    Promise.all([membersFetch, invitationsFetch])
      .then(([{ data }, invitations]) => {
        const pending: GatedCommunityMemberRecord[] = invitations
          .filter((inv) => inv.communities.some((c) => c.communityId === gatedCommunityId))
          .map((inv) => ({
            ...inv,
            id: inv.userId,
            profile: null,
            communities: []
          }))

        const seen = new Set<string>(data.map((m) => m.email))
        const merged = [...data, ...pending.filter((p) => !seen.has(p.email))]
        setAllMembers(merged)
      })
      .catch(() => setAllMembers([]))
      .finally(() => setLoading(false))
  }, [gatedCommunityId, refreshKey, enabled, isOrgAdmin, companyId])

  const filtered = useMemo(
    () => filterMembersByQuery(filterMembersByRoles(allMembers, roles), name),
    [allMembers, name, roles]
  )
  const sorted = useMemo(() => sortMembersByField(filtered, orderBy, order), [filtered, orderBy, order])
  const members = useMemo(() => sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage), [sorted, page, rowsPerPage])

  return { members, totalCount: filtered.length, loading }
}
